import React, { FC, useCallback, useEffect, useRef } from "react";
import {
	AlternativeProductGroup,
	useAlternativeProducts,
} from "../../../hooks/useAlternativeProducts";
import { useBottomMenu } from "../../../hooks/useBottomMenu";
import { useHasBottomRootMenu } from "../../../hooks/useHasBottomRootMenu";
import { useProductOptions } from "../../../hooks/useProductOptions";
import { useSelectedAlternativeProductGroupState } from "../../bottom-menu-panels/alternative-products/hooks";
import { BottomMenuButton } from "../../bottom-menu/bottom-menu-button";
import { BottomMenuButtons, IBottomMenuButtonsRef } from "../../bottom-menu/buttons";
import { BottomMenuIds, BottomMenuPaths } from "../../bottom-menu/paths";
import { SvgIcon } from "../../svg-icon";
import "shared/styles/product-options.css";
import { useLabels } from "../../../hooks/useLabels";
import { useSupportedProductStartDate } from "../../../hooks/useSupportedProductStartDate";
import { labels } from "./labels";

interface IStartDateOptionButton {
	onStartDateOptionClick: () => void;
	showIconButtons: boolean;
}

const StartDateOptionButton: FC<IStartDateOptionButton> = ({
	onStartDateOptionClick,
	showIconButtons,
}) => {
	const { stack } = useBottomMenu();

	const supportedStartDate = useSupportedProductStartDate();
	const isDateSelectionMenuOpen = stack.some((s) => s.id === BottomMenuIds.DateSelection);

	const [[startDateText]] = useLabels([
		supportedStartDate === "year" ? labels.startYear : labels.startMonth,
	]);

	return (
		<BottomMenuButton
			onClick={onStartDateOptionClick}
			title={startDateText}
			isActive={isDateSelectionMenuOpen}
			iconName={showIconButtons ? "calendar" : undefined}
		/>
	);
};

interface IAlternativeProductsButtons {
	alternativeProductsGroups: AlternativeProductGroup[];
	showIconButtons: boolean;
	onAlternativeProductClick: (path: string, index: number) => void;
}

const AlternativeProductsButtons: FC<IAlternativeProductsButtons> = ({
	alternativeProductsGroups,
	showIconButtons,
	onAlternativeProductClick,
}) => {
	const { currentPath } = useBottomMenu();

	const [alternativeProductGroup, setAlternativeProductGroup] =
		useSelectedAlternativeProductGroupState();

	const handleAlternativeProductClick = useCallback(
		(group: AlternativeProductGroup, index: number) => {
			setAlternativeProductGroup(group);
			onAlternativeProductClick(
				currentPath === BottomMenuPaths.AlternativeProducts &&
					alternativeProductGroup?.id === group.id
					? BottomMenuPaths.Options
					: BottomMenuPaths.AlternativeProducts,
				index,
			);
		},
		[
			alternativeProductGroup?.id,
			currentPath,
			onAlternativeProductClick,
			setAlternativeProductGroup,
		],
	);

	return (
		<>
			{alternativeProductsGroups?.map((group, index) => (
				<BottomMenuButton
					onClick={() => handleAlternativeProductClick(group, index)}
					key={group.id}
					title={group.name}
					// can't use id of group since it is generated on each api call
					isActive={
						alternativeProductGroup?.name === group.name &&
						currentPath === BottomMenuPaths.AlternativeProducts
					}
				>
					{showIconButtons ? <SvgIcon svgUrl={group.icon} /> : undefined}
				</BottomMenuButton>
			))}
		</>
	);
};

interface IProductOptionGroupButtons {
	productOptionGroup: eXpress.core.IProductOptionGroup;
	showIconButtons: boolean;
	onProductOptionsGroupClick: () => void;
}

const ProductOptionsGroupButtons: FC<IProductOptionGroupButtons> = ({
	productOptionGroup,
	showIconButtons,
	onProductOptionsGroupClick,
}) => {
	const { currentPath } = useBottomMenu();

	return (
		<BottomMenuButton
			onClick={onProductOptionsGroupClick}
			key={productOptionGroup.name}
			title={productOptionGroup.name}
			isActive={currentPath === BottomMenuPaths.ProductOptions}
		>
			{showIconButtons && productOptionGroup.icon ? (
				<SvgIcon svgUrl={productOptionGroup.icon} />
			) : undefined}
		</BottomMenuButton>
	);
};

export const OptionsBottomMenu = () => {
	const { gotoPath, currentPath } = useBottomMenu();
	const hasRootMenu = useHasBottomRootMenu();
	const bottomMenuRef = useRef<IBottomMenuButtonsRef>(null);

	const { alternativeProductsGroups } = useAlternativeProducts();
	const { productOptionGroup } = useProductOptions();
	const supportedStartDate = useSupportedProductStartDate();

	const hasOptions =
		alternativeProductsGroups?.length || productOptionGroup || supportedStartDate;

	// only show icons if all kind of 'options' are configured with icons
	// - alternative product groups
	// - product option group
	// - startDate option (omitted here because this option always has a icon)
	const showIconButtons =
		(!alternativeProductsGroups?.length || // ignore if there are no alternative product groups
			alternativeProductsGroups.every((group) => group.icon)) &&
		(!productOptionGroup || // ignore if there is no product option group
			!!productOptionGroup.icon);

	const onMenuItemClick = useCallback(
		(path: string, index: number) => {
			gotoPath(path);
			// + 1 to take backbutton into account
			bottomMenuRef.current?.scrollIntoView(index + 1);
		},
		[gotoPath],
	);

	useEffect(() => {
		if (!hasOptions) {
			gotoPath(BottomMenuIds.Root);
		}
	}, [hasOptions, gotoPath]);

	return (
		<BottomMenuButtons
			ref={bottomMenuRef}
			showBackButton={hasRootMenu}
			onBack={() => gotoPath(BottomMenuPaths.Root)}
		>
			{supportedStartDate && (
				<StartDateOptionButton
					showIconButtons={showIconButtons}
					onStartDateOptionClick={() =>
						onMenuItemClick(
							currentPath === BottomMenuPaths.DateSelection
								? BottomMenuPaths.Options
								: BottomMenuPaths.DateSelection,
							0,
						)
					}
				/>
			)}
			{!!alternativeProductsGroups?.length && (
				<AlternativeProductsButtons
					alternativeProductsGroups={alternativeProductsGroups}
					onAlternativeProductClick={(path, index) =>
						onMenuItemClick(path, index + (supportedStartDate ? 1 : 0))
					}
					showIconButtons={showIconButtons}
				/>
			)}
			{productOptionGroup && (
				<ProductOptionsGroupButtons
					onProductOptionsGroupClick={() =>
						onMenuItemClick(
							currentPath === BottomMenuPaths.ProductOptions
								? BottomMenuPaths.Options
								: BottomMenuPaths.ProductOptions,
							(alternativeProductsGroups?.length ?? 0) + (supportedStartDate ? 1 : 0),
						)
					}
					productOptionGroup={productOptionGroup}
					showIconButtons={showIconButtons}
				/>
			)}
		</BottomMenuButtons>
	);
};
